import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import Layout4 from "../DataLayouts/Layout4";
import {
  createApplicationRole,
  getApplicationRole,
  updateApplicationRole,
} from "../../Apis/ApplicationAdminUserApis";

export default function ApplicationUserRoleForm({ mode = "add" }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const isView = mode === "view";
  const isEdit = mode === "edit";

  const [form, setForm] = useState({ user_role_name: "", description: "" });
  const [loading, setLoading] = useState(isEdit || isView);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!(isEdit || isView) || !id) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const role = await getApplicationRole(id);
        if (cancelled || !role) return;
        setForm({
          user_role_name: role.user_role_name || role.role_name || "",
          description: role.description || "",
        });
      } catch (err) {
        toast.error(err.message || "Failed to load role");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, isEdit, isView]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isView) return;
    if (!form.user_role_name.trim()) {
      toast.error("Role name is required.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        user_role_name: form.user_role_name.trim(),
        description: form.description.trim(),
      };
      if (isEdit) {
        await updateApplicationRole(id, payload);
      } else {
        await createApplicationRole(payload);
      }
      toast.success(isEdit ? "Role updated" : "Role created");
      navigate("/application/user-roles");
    } catch (err) {
      toast.error(err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const title = isView ? "View role" : isEdit ? "Edit role" : "Add role";

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 640 }}>
      <Layout4
        heading={title}
        subtitle="Application user role"
        showAddButton={false}
      />
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={32} />
        </Box>
      ) : (
        <Box component="form" onSubmit={handleSubmit}>
          <Stack spacing={2}>
            <TextField
              label="Role name"
              name="user_role_name"
              value={form.user_role_name}
              onChange={onChange}
              disabled={isView}
              required
              fullWidth
            />
            <TextField
              label="Description"
              name="description"
              value={form.description}
              onChange={onChange}
              disabled={isView}
              fullWidth
              multiline
              minRows={2}
            />
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                onClick={() => navigate("/application/user-roles")}
              >
                Back
              </Button>
              {!isView && (
                <Button type="submit" variant="contained" disabled={saving}>
                  {saving ? "Saving…" : "Save"}
                </Button>
              )}
            </Stack>
          </Stack>
        </Box>
      )}
    </Box>
  );
}
